// Mapbox Tilequery-based POI lookup + walking-distance estimate.
//
// Uses the Mapbox Streets v8 poi_label layer (free under standard tilequery
// quotas) so we don't pay for Geocoding / Directions on every listing view.
// Walking distance is estimated from the straight-line haversine distance
// multiplied by a routing-detour factor.

const TILEQUERY_URL = 'https://api.mapbox.com/v4/mapbox.mapbox-streets-v8/tilequery';
const DIRECTIONS_URL = 'https://api.mapbox.com/directions/v5/mapbox/walking';

// Average pedestrian speed used by Mapbox routing (~5 km/h).
const WALKING_METERS_PER_MINUTE = 80;
// Real walking paths are not straight; bump distance by this factor
// to convert as-the-crow-flies into approximate routed distance.
const ROUTING_DETOUR_FACTOR = 1.3;

// Train-family makis that we surface prominently (Uber-style chip).
const TRAIN_MAKIS = new Set(['rail', 'rail-metro', 'rail-light', 'tram', 'entrance']);

// Map Mapbox Streets v8 features to our UI buckets. Some categories
// live in `poi_label` (food, shopping, parking) and transit lives in a
// separate `transit_stop_label` layer where `class` is null.
//
// Returns a bucket key or null when the feature is not interesting.
const bucketForFeature = (props, sourceLayer) => {
  if (sourceLayer === 'transit_stop_label') {
    return TRAIN_MAKIS.has(props.maki) ? 'train' : 'transit';
  }

  const cls = props.class;
  const maki = props.maki;

  if (cls === 'food_and_drink') {
    // Skip very generic / fast-food-only makis to keep the list tasteful.
    if (['restaurant', 'cafe', 'bakery', 'bar'].includes(maki)) return 'food';
    if (!maki || maki === 'fast-food') return 'food';
    return 'food';
  }
  if (cls === 'food_and_drink_stores') {
    return 'shopping';
  }
  if (cls === 'motorist' && (maki === 'parking' || maki === 'fuel')) {
    return maki === 'parking' ? 'parking' : null;
  }
  return null;
};

const toRadians = deg => (deg * Math.PI) / 180;

// Haversine distance in meters between two [lon, lat] points.
export const haversineMeters = (a, b) => {
  const R = 6371000;
  const dLat = toRadians(b[1] - a[1]);
  const dLon = toRadians(b[0] - a[0]);
  const lat1 = toRadians(a[1]);
  const lat2 = toRadians(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const walkingMinutes = straightMeters => {
  const adjusted = straightMeters * ROUTING_DETOUR_FACTOR;
  return Math.max(1, Math.round(adjusted / WALKING_METERS_PER_MINUTE));
};

/**
 * Fetch nearby POIs and group them into named buckets, returning the
 * closest entry per bucket. Returns an empty array when no token or no
 * geolocation is supplied so callers can render nothing gracefully.
 *
 * @param {{ lat: number, lng: number }} center
 * @param {string} accessToken Mapbox public token
 * @param {{ radius?: number, limit?: number, signal?: AbortSignal }} [opts]
 * @returns {Promise<Array<{ bucket: string, name: string, maki: string, meters: number, minutes: number }>>}
 */
export const fetchNearbyPois = async (center, accessToken, opts = {}) => {
  if (!accessToken || !center || center.lat == null || center.lng == null) {
    return [];
  }
  const { radius = 1500, limit = 50, signal } = opts;
  const url =
    `${TILEQUERY_URL}/${center.lng},${center.lat}.json` +
    `?radius=${radius}&limit=${limit}&dedupe=true` +
    `&layers=poi_label,transit_stop_label` +
    `&access_token=${accessToken}`;

  let res;
  try {
    res = await fetch(url, { signal });
  } catch (e) {
    if (e.name === 'AbortError') return [];
    return [];
  }
  if (!res.ok) return [];
  const json = await res.json();
  const features = Array.isArray(json.features) ? json.features : [];

  // Group by bucket, keep the closest in each.
  const closestPerBucket = {};
  for (const feature of features) {
    const props = feature.properties || {};
    const sourceLayer = feature.properties?.tilequery?.layer;
    const bucket = bucketForFeature(props, sourceLayer);
    if (!bucket) continue;
    const coords = feature.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) continue;
    const meters = haversineMeters([center.lng, center.lat], coords);

    const existing = closestPerBucket[bucket];
    if (!existing || meters < existing.meters) {
      closestPerBucket[bucket] = {
        bucket,
        name: props.name || props.name_en || '',
        maki: props.maki || '',
        meters,
        minutes: walkingMinutes(meters),
      };
    }
  }

  // Stable ordering: train first (most prominent / commuter signal), then bus
  // transit, food, shopping, parking. Buckets without a hit are omitted.
  const ORDER = ['train', 'transit', 'food', 'shopping', 'parking'];
  const ordered = ORDER.map(k => closestPerBucket[k]).filter(Boolean);
  // Attach the matched feature coordinates so callers can route to them
  // without re-querying.
  for (const item of ordered) {
    const match = features.find(f => {
      const props = f.properties || {};
      const layer = props.tilequery?.layer;
      const bucket = bucketForFeature(props, layer);
      if (bucket !== item.bucket) return false;
      const c = f.geometry?.coordinates;
      if (!Array.isArray(c)) return false;
      return Math.abs(haversineMeters([center.lng, center.lat], c) - item.meters) < 1;
    });
    if (match?.geometry?.coordinates) {
      const [lng, lat] = match.geometry.coordinates;
      item.lng = lng;
      item.lat = lat;
    }
  }
  return ordered;
};

/**
 * Fetch a walking route between two [lng,lat] points using Mapbox Directions.
 * Returns the GeoJSON LineString of the path plus distance/duration; returns
 * null on failure so callers can render the map without a route.
 *
 * @param {[number, number]} from [lng, lat]
 * @param {[number, number]} to   [lng, lat]
 * @param {string} accessToken Mapbox public token
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ line: object, meters: number, seconds: number } | null>}
 */
export const fetchWalkingRoute = async (from, to, accessToken, opts = {}) => {
  if (!accessToken || !Array.isArray(from) || !Array.isArray(to)) return null;
  const { signal } = opts;
  const coords = `${from[0]},${from[1]};${to[0]},${to[1]}`;
  const url =
    `${DIRECTIONS_URL}/${coords}?geometries=geojson&overview=full` + `&access_token=${accessToken}`;
  let res;
  try {
    res = await fetch(url, { signal });
  } catch (e) {
    return null;
  }
  if (!res.ok) return null;
  const json = await res.json();
  const route = json?.routes?.[0];
  if (!route?.geometry?.coordinates?.length) return null;
  return {
    line: { type: 'Feature', geometry: route.geometry, properties: {} },
    meters: route.distance,
    seconds: route.duration,
  };
};
